import angular from 'angular';
import _ from 'lodash';

import roles from 'app/validators/roles';
import { checkAccess } from 'app/actions/app.actions';
import retailerGroupsExistingUserField from 'retailer-groups/components/existing-user-field/retailer-groups-existing-user-field-module';

import { fetchOwnerValidationMethod } from 'registration/actions/fetch-owner-validation.actions';
import { fetchPrivilegeTemplates } from 'privilege-templates/actions/fetch-privilege-templates.actions';
import privilegeApi from 'privilege-templates/resources/privilege-templates.api';
import helpers from 'privilege-templates/logic/helpers';
import { fetchPrivilegeDisplayTypesDictionary } from 'app/actions/dictionaries/privilege-display-types-dictionary.actions';
import utils from 'utils/lsp-utils-service';
import * as api from '../../resources/user.api';

import createAccountStepsTemplate from './create-account-steps.html';
import createAccountSubmitDialogTemplate from './dialog/create-account-submit-dialog.html';

const MODE_STANDARD = 'standard';
const MODE_SECONDARY = 'secondary';

const RETAILER_PRIMARY = 'RETAILER_PRIMARY';
const RETAILER_SECONDARY = 'RETAILER_SECONDARY';

const RETAILER_TYPES = {
  INDEPENDENT: 'INDEPENDENT',
  CHAIN_SUBORDINATE: 'CHAIN_SUBORDINATE',
  CHAIN_HEAD: 'CHAIN_HEAD',
  SUPER_CHAIN: 'SUPER_CHAIN',
};

const getOwnerValidationMethod = (validationMethod, locale) => {
  if (validationMethod[locale]) return validationMethod[locale][0];
  return validationMethod;
};

const mapStateToThis = ({ dictionary, session, registration, config, privilegeTemplates }) => {
  const { locale } = session;
  const { ownerValidationMethod } = registration;
  const { jurisdictionId, lowCostCarriers, phoneMask, phoneLength, maxFirstNameLength, maxLastNameLength, maxEmailLength } = config;
  const terms = dictionary.terms[locale];

  return {
    terms,
    session,
    locale,
    ownerValidationMethod: getOwnerValidationMethod(ownerValidationMethod, locale),
    jurisdictionId,
    lowCostCarriers,
    phoneMask,
    phoneLength,
    maxFirstNameLength,
    maxLastNameLength,
    maxEmailLength,
    privilegeTemplates,
  };
};

const mapDispatchToThis = {
  fetchPrivilegeDisplayTypesDictionary,
  fetchOwnerValidationMethod,
  fetchPrivilegeTemplates,
  checkAccess,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', '$scope', 'gettextCatalog', 'Notifications', 'Responsive')
class CreateAccountStepsController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  async $onInit() {
    await this.fetchPrivilegeDisplayTypesDictionary();
    await this.initializeConfiguration();

    if (!this.session.isAdmin) {
      const userId = this.session.id;
      const data = await api.fetchUsersReportingTo(userId);
      const superChain = this.isSuperChain ? this.getSuperChain() : [];
      if (data && data.length > 0) {
        this.reportTo = [...data, ...[superChain]];
        this.reportToTpl = [...data, ...[superChain]];
      } else {
        this.mode = MODE_SECONDARY;
      }

      this.filteredUserClasses(data || []).then(({ retailerType, userClass }) => {
        this.initializeUserClasses(retailerType, userClass);
        if (userClass === RETAILER_PRIMARY) {
          this.user.retailerId = data.retailerId;
        }
      });
    } else {
      this.initializeUserClasses();
    }
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async initializeConfiguration() {
    this.stepsTitles = {
      1: this.gettextCatalog.getString('User Type'),
      2: this.gettextCatalog.getString('Personal Information'),
      3: this.gettextCatalog.getString('Set Permissions'),
    };

    this.step = 1;
    this.numberOfSteps = 3;
    this.salts = null;
    this.tmp = {};
    this.reportTo = [];
    this.reportToTpl = [];
    this.showReportTo = {};
    this.incorrectRetailerNo = false;
    this.validateRetailerError = false;
    this.mode = MODE_STANDARD;
    this.isSuperChain = this.session.retailer.retailerType === RETAILER_TYPES.SUPER_CHAIN;
    this.user = {
      contact: {},
      siteId: this.jurisdictionId,
    };

    await this.fetchOwnerValidationMethod();
  }

  getSuperChain() {
    const { retailer } = this.session;
    const { retailerDisplayNumber, retailerType } = retailer;

    return {
      retailerId: retailerDisplayNumber,
      retailerType,
    };
  }

  filteredUserClasses(data = []) {
    if (data.length > 0) {
      data = data.map((item) => item.retailerType);
      return Promise.resolve({
        retailerType: data,
        userClass: [],
      });
    }
    this.primaryUserId =
      roles.SECONDARY_USER_ROLES.indexOf(_.get(this, 'session.role')) !== -1 ||
      roles.PRIMARY_USER_ROLES.indexOf(_.get(this, 'session.role')) !== -1
        ? _.get(this, 'session.id')
        : null;
    return this.loadPrimaryUser().then((data) =>
      Promise.resolve({
        retailerType: [data.primaryRetailer.retailerType],
        userClass: roles.SECONDARY_USER_ROLES,
      })
    );
  }

  initializeUserClasses(filteredRetailerTypes = [], filteredUserClasses = []) {
    this.userClasses = this.getUserClasses(filteredRetailerTypes, filteredUserClasses);
    this.userClassesGroupedById = this.getUserClassesGroupedById(this.userClasses);

    if (this.userClasses.length === 1) {
      this.user.userClass = this.userClasses[0];
      this.step = 2;
      this.setUserClass();
    }
  }

  async setUserClass() {
    this.parseReportTo();
    await this.validateRetailerNo();

    await this.fetchPrivilegeTemplates(this.user.userClass.id, this.user.userClass.type);
    this.privileges = await privilegeApi.fetchPrivileges(this.user.userClass.id);
    this.privilegesTree = helpers.createTree(this.privileges, 'displayPath');
    if (this.isUserPrimary() && this.privilegeTemplates.selectedId) {
      this.user.templateId = this.privilegeTemplates.selectedId;
      this.templateChange();
    }
  }

  parseReportTo() {
    if (this.isSuperChain) {
      this.reportTo = this.reportToTpl;
    } else {
      this.reportTo = this.reportToTpl.filter((i) => i.retailerType === this.user.userClass.type);
    }

    if (this.reportTo.length > 0) {
      Object.keys(this.showReportTo).forEach((key) => {
        this.showReportTo[key] = false;
      });
      this.showReportTo[this.user.userClass.type] = true;
    }

    if (
      _.get(this, 'user.userClass.id') === RETAILER_SECONDARY &&
      this.isUserChainSubordinate() &&
      _.get(this, 'session.retailer.isChainSubordinate')
    ) {
      this.reportTo.push({
        retailerId: _.get(this, 'session.retailer.retailerId'),
        retailerType: _.get(this, 'session.retailer.retailerType'),
      });
      this.reportTo = _.sortBy(this.reportTo, ['retailerId']);
    }
  }

  showOwnerValidationMethod() {
    return this.isUserIndependentRetailer() && this.isUserPrimary();
  }

  showRetailerNumber() {
    return (
      (this.isUserIndependentRetailer() && this.showReportTo[RETAILER_TYPES.INDEPENDENT]) ||
      (this.checkAccess('user.shouldProvideRetailerId') && this.isUserRetailer())
    );
  }

  showChainId() {
    return this.isUserChainHead() && this.showReportTo[RETAILER_TYPES.CHAIN_HEAD];
  }

  showChainSubordinateId() {
    return this.isUserChainSubordinate() && this.showReportTo[RETAILER_TYPES.CHAIN_SUBORDINATE];
  }

  isUserRetailer() {
    return (
      this.user &&
      this.user.userClass &&
      (this.user.userClass.type === RETAILER_TYPES.INDEPENDENT ||
        this.user.userClass.type === RETAILER_TYPES.CHAIN_SUBORDINATE ||
        this.user.userClass.type === RETAILER_TYPES.CHAIN_HEAD)
    );
  }

  isUserIndependentRetailer() {
    return this.user && this.user.userClass && this.user.userClass.type === RETAILER_TYPES.INDEPENDENT;
  }

  isUserChainRetailer() {
    return (
      this.user &&
      this.user.userClass &&
      (this.user.userClass.type === RETAILER_TYPES.CHAIN_HEAD || this.user.userClass.type === RETAILER_TYPES.CHAIN_SUBORDINATE)
    );
  }

  isUserChainHead() {
    return this.user && this.user.userClass && this.user.userClass.type === RETAILER_TYPES.CHAIN_HEAD;
  }

  isUserChainSubordinate() {
    return this.user && this.user.userClass && this.user.userClass.type === RETAILER_TYPES.CHAIN_SUBORDINATE;
  }

  isUserPrimary() {
    return this.user && this.user.userClass && roles.PRIMARY_USER_ROLES.includes(this.user.userClass.id);
  }

  isUserSecondary() {
    return this.user && this.user.userClass && roles.SECONDARY_USER_ROLES.includes(this.user.userClass.id);
  }

  isUserAdmin() {
    return this.user && this.user.userClass && roles.ADMIN_ROLES.includes(this.user.userClass.id);
  }

  getUserClassesGroupedById(userClasses) {
    return _.groupBy(userClasses, 'id');
  }

  getUserClasses(filteredRetailerTypes, filteredUserClasses) {
    let userClasses = [
      {
        label: this.gettextCatalog.getString('Administrator'),
        subLabel: this.gettextCatalog.getString(''),
        description: [
          this.gettextCatalog.getString('Lottery employee managing or working within system'),
          this.gettextCatalog.getString(
            'May have full access to system features, including managing content and users, or limited access based on privileges granted'
          ),
        ],
        type: null,
        id: _.get(roles.ADMIN_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Independent Retailer'),
        subLabel: this.gettextCatalog.getString('Primary User'),
        description: [
          this.gettextCatalog.getString('Independent store owner or co-owner'),
          this.gettextCatalog.getString('Has access to all financial information, reports, and functions'),
          this.gettextCatalog.getString('Manages accounts of Independent Retailer Secondary users for that retail location'),
          this.gettextCatalog.getString('Manages Retailer Settings'),
        ],
        type: RETAILER_TYPES.INDEPENDENT,
        id: _.get(roles.PRIMARY_USER_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Chain Subordinate Retailer'),
        subLabel: this.gettextCatalog.getString('Primary User'),
        description: [
          this.gettextCatalog.getString('Chain store owner, franchisee, or chain store manager'),
          this.gettextCatalog.getString('Has access to all financial information, reports, and functions for that retail location'),
          this.gettextCatalog.getString('Manages accounts of Chain Subordinate Secondary users for that location'),
          this.gettextCatalog.getString('Manages Retailer Settings'),
        ],
        type: RETAILER_TYPES.CHAIN_SUBORDINATE,
        id: _.get(roles.PRIMARY_USER_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Chain Head Retailer'),
        subLabel: this.gettextCatalog.getString('Primary User'),
        description: [
          this.gettextCatalog.getString('Corporate-level manager of a chain of retailers'),
          this.gettextCatalog.getString(
            'Has access to all financial information and reports about chain and individual locations within the chain'
          ),
          this.gettextCatalog.getString('Manages accounts of Chain Head Secondary users for that chain'),
          this.gettextCatalog.getString(
            'Manages Retailer Settings for chain, including whether Chain Subordinate Primary retailers can self-register'
          ),
        ],
        type: RETAILER_TYPES.CHAIN_HEAD,
        id: _.get(roles.PRIMARY_USER_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Independent Retailer'),
        subLabel: this.gettextCatalog.getString('Secondary User'),
        description: [
          this.gettextCatalog.getString('Manager, clerk, or other employee working at retail location'),
          this.gettextCatalog.getString('Has a limited access to system features based on privileges granted by Primary User'),
        ],
        type: RETAILER_TYPES.INDEPENDENT,
        id: _.get(roles.SECONDARY_USER_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Chain Subordinate Retailer'),
        subLabel: this.gettextCatalog.getString('Secondary User'),
        description: [
          this.gettextCatalog.getString('Manager, clerk, or other employee working at chain retail location'),
          this.gettextCatalog.getString('Has limited access to system features based on privileges granted by Primary User'),
        ],
        type: RETAILER_TYPES.CHAIN_SUBORDINATE,
        id: _.get(roles.SECONDARY_USER_ROLES, '[0]'),
      },
      {
        label: this.gettextCatalog.getString('Chain Head Retailer'),
        subLabel: this.gettextCatalog.getString('Secondary User'),
        description: [
          this.gettextCatalog.getString('Corporate-level employee of a chain of retailers'),
          this.gettextCatalog.getString('Has access to a limited scope of information, based on privileges granted by Primary User'),
        ],
        type: RETAILER_TYPES.CHAIN_HEAD,
        id: _.get(roles.SECONDARY_USER_ROLES, '[0]'),
      },
    ];

    if (filteredRetailerTypes.length > 0) {
      userClasses = userClasses.filter(
        (userClass) =>
          filteredRetailerTypes.indexOf(userClass.type) > -1 ||
          (userClass.id === _.get(roles.SECONDARY_USER_ROLES, '[0]') && userClass.type === this.session.retailer.retailerType)
      );
    }

    if (filteredUserClasses.length > 0) {
      userClasses = userClasses.filter((userClass) => {
        if (filteredUserClasses.indexOf(userClass.id) > -1) {
          return userClass;
        }
      });
    }

    return userClasses;
  }

  loadPrimaryUser() {
    if (this.mode === MODE_SECONDARY) {
      return api.loadUserById(this.primaryUserId);
    }
    return Promise.resolve(null);
  }

  cancelForm() {
    this.form.$setDirty();
    this.$state.go('user-manageUsers');
  }

  submitForm(form) {
    form.$setSubmitted();

    if (form.$invalid || this.incorrectRetailerNo || this.validateRetailerError) {
      return;
    }

    if (this.isUserAdmin() && this.isLimitedToRetailers() && !this.user.lotteryRepId) {
      this.Notifications.showAlert(
        this.gettextCatalog.getString('Failed!'),
        this.gettextCatalog.getString('Admin user has at least one limit to retailers privilege and not set {{ dsr }} ID.', {
          dsr: this.terms.DSR.singular,
        })
      );
      return;
    }

    const user = _.cloneDeep(this.user);
    this.Notifications.showComplexDialog({
      template: createAccountSubmitDialogTemplate,
      resolve: {
        user: () => user,
        presetName: () => _.get(this, `currentPreset.translations[${this.locale}].name`),
        phoneMask: () => this.phoneMask,
      },
    }).result.then(() => this.submitConfirm());
  }

  submitConfirm() {
    const { retailer } = this.session;
    const { retailerDisplayNumber } = retailer;
    if (this.salts) this.user.primaryRetailer.hashedPins = utils.getHashes(this.user.primaryRetailer.owner, this.salts);

    return api.createUser(this.user, this.locale, retailerDisplayNumber).then(() => {
      this.Notifications.showAlert(
        this.gettextCatalog.getString('Success!'),
        this.gettextCatalog.getString('We have emailed {{name}} a link to complete the registration process.', {
          name: this.user.contact.emailAddress,
        })
      );
      this.$state.go('user-manageUsers', { skipDirtyForms: true });
    });
  }

  editPresetPrivileges(privileges) {
    this.user.privilegeIds = this.filterPrivileges(privileges);
  }

  templateChange() {
    const preset = this.privilegeTemplates.entities[this.user.templateId];
    if (preset) {
      this.currentPreset = _.cloneDeep(preset);
      this.user.privilegeIds = this.filterPrivileges(preset.privileges);
    }
  }

  filterPrivileges(privileges) {
    return privileges.filter((privilegeName) => this.privileges.find((priv) => priv.id === privilegeName));
  }

  isLimitedToRetailers() {
    return this.user.privilegeIds.find((privilegeId) => privilegeId.endsWith('limitToRetailers')) || false;
  }

  async validateRetailerNo() {
    if (this.user.primaryRetailer && this.user.primaryRetailer.retailerDisplayNumber) {
      try {
        const retailer = await api.fetchRetailerDetailsForSecondary(
          this.user.primaryRetailer.retailerDisplayNumber,
          this.isUserSecondary()
        );
        // check if userClass chain head and retailer super chain
        if (retailer) {
          this.salts = retailer.salts;
          this.isSuperChain =
            retailer.retailerType === RETAILER_TYPES.SUPER_CHAIN && this.user.userClass.type === RETAILER_TYPES.CHAIN_HEAD;
          this.incorrectRetailerNo = !!retailer.retailerType && retailer.retailerType !== this.user.userClass.type && !this.isSuperChain;
		  this.validateRetailerError = false;
        }
      } catch (error) {
        this.validateRetailerError = true;
      }
    }
  }

  async next(form) {
    if (this.step === 2) {
      form.$setSubmitted();
      await this.validateRetailerNo();
      if (form.$invalid || this.incorrectRetailerNo || this.validateRetailerError) {
        return;
      }
    }
    form.$setPristine();
    form.$setUntouched();
    this.step += 1;
    this.$scope.$apply();
  }

  previous() {
    this.step -= 1;
    if (this.step === 1) {
      this.user.userClass = this.userClasses.find(
        (userClass) => userClass.id === this.user.userClass.id && userClass.type === this.user.userClass.type
      );
    }
    this.$scope.$apply();
  }
}

export class CreateAccountStepsComponent {
  constructor() {
    this.template = createAccountStepsTemplate;
    this.controller = CreateAccountStepsController;
    return this;
  }
}

export default angular
  .module('create-account-steps', [retailerGroupsExistingUserField.name])
  .component('createAccountSteps', new CreateAccountStepsComponent());



// WEBPACK FOOTER //
// ./src/modules/user/components/create-account-steps/create-account-steps-component.js